import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Image } from "@chakra-ui/react";
import HaikeiB from "../images/Background/HaikeiB.jpg";
import { useInView } from "react-intersection-observer";
import TicketModal from "./TicketModal";
import TicketSection from "./TicketSection";
import PerformerSection from "./PerformerSection";
import ScheduleHeader from "./ScheduleHeader";

const commonStyles = {
  container: {
    position: "relative" as const,
    width: "100%",
    height: "auto",
    minHeight: "100vh",
  },
  backgroundImage: {
    width: "100%",
    height: "auto",
    minHeight: "100%",
    objectFit: "cover" as const,
    position: "absolute" as const,
    top: "0",
    left: "0",
    zIndex: -1,
  },
  contentContainer: {
    position: "relative" as const,
    width: "100%",
    paddingTop: {
      base: "15%",
      sm: "12%",
      md: "10%",
      lg: "8%",
    },
    paddingBottom: {
      base: "20%",
      sm: "15%",
      md: "10%",
      lg: "8%",
    },
  },
};

const animationVariants = {
  slideIn: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
    none: { display: "none" },
  },
  scaleFadeIn: {
    hidden: { opacity: 0, scale: 1.1 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    instant: { opacity: 1, scale: 1, transition: { duration: 0 } },
    none: { display: "none" },
  },
  slideInFromLeft: {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1.2, ease: "easeInOut" },
    },
    instant: { opacity: 1, x: 0, transition: { duration: 0 } },
    none: { display: "none" },
  },
};

const Schedule = () => {
  const prevScrollY = useRef(0);
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");

  const scheduleRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { ref: componentRef } = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [elementsAnimatedState] = useState({
    scheduleImage: false,
    scheduleContent: false,
    ticket: false,
    ticketContent: false,
    performer: false,
    performerTitle: false,
    performerImage: false,
    performerText: false,
    performerOrchestra: false,
    ticketContent8: false,
    ticketContent9: false,
    ticketContent10: false,
  });

  const inViewStateRef = useRef({ ...elementsAnimatedState });

  const useInViewConfig = useMemo(
    () => ({ triggerOnce: false, threshold: 0.3 }),
    []
  );
  const useInViewConfigContent = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.1,
      rootMargin: "0px 0px 150px 0px",
    }),
    []
  );

  const storyImageRef = useInView(useInViewConfig);
  const headingRef = useInView(useInViewConfig);
  const ticketRef = useInView(useInViewConfig);
  const ticketContentRef = useInView(useInViewConfigContent);
  const performerRef = useInView(useInViewConfig);
  const performerTitleRef = useInView(useInViewConfig);
  const performerImageRef = useInView({ ...useInViewConfig, threshold: 0.5 });
  const performerTextRef = useInView(useInViewConfig);
  const performerOrchestraRef = useInView(useInViewConfig);
  const generalSaleTitleRef = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 100px 0px",
  });
  const generalSaleContentRef = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 80px 0px",
  });
  const generalSaleButtonRef = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 60px 0px",
  });
  const ticketContent8Ref = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 100px 0px",
  });
  const ticketContent9Ref = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 80px 0px",
  });
  const ticketContent10Ref = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 60px 0px",
  });
  const ticketContent11Ref = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 60px 0px",
  });
  const ticketContent16Ref = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 60px 0px",
  });

  const refs = useMemo(
    () => ({
      storyImageRef,
      headingRef,
      ticketRef,
      ticketContentRef,
      performerRef,
      performerTitleRef,
      performerImageRef,
      performerTextRef,
      performerOrchestraRef,
      generalSaleTitleRef,
      generalSaleContentRef,
      generalSaleButtonRef,
      ticketContent8Ref,
      ticketContent9Ref,
      ticketContent10Ref,
      ticketContent11Ref,
      ticketContent16Ref,
    }),
    [
      storyImageRef,
      headingRef,
      ticketRef,
      ticketContentRef,
      performerRef,
      performerTitleRef,
      performerImageRef,
      performerTextRef,
      performerOrchestraRef,
      generalSaleTitleRef,
      generalSaleContentRef,
      generalSaleButtonRef,
      ticketContent8Ref,
      ticketContent9Ref,
      ticketContent10Ref,
      ticketContent11Ref,
      ticketContent16Ref,
    ]
  );

  useEffect(() => {
    Object.entries(refs).forEach(([key, { inView }]) => {
      inViewStateRef.current[key] = inView;
    });
  }, [refs]);

  const getAnimationState = useCallback(
    (inView: boolean, hasAnimated: boolean) => {
      if (scrollDirection === "down") {
        return inView ? "visible" : hasAnimated ? "visible" : "hidden";
      } else if (scrollDirection === "up") {
        return inView ? "instant" : "hidden";
      }
      return hasAnimated ? "visible" : "hidden";
    },
    [scrollDirection]
  );

  const animationStates = useMemo(() => {
    return Object.fromEntries(
      Object.entries(refs).map(([key, { inView }]) => [
        key.replace(/Ref$/, ""),
        getAnimationState(
          inView,
          elementsAnimatedState[key.replace(/Ref$/, "")]
        ),
      ])
    );
  }, [refs, elementsAnimatedState, getAnimationState]);

  // アニメーションステートの型を修正
  const typedAnimationStates = useMemo(() => {
    return Object.fromEntries(
      Object.entries(animationStates).map(([key, value]) => [
        key,
        value as "visible" | "hidden" | "instant",
      ])
    );
  }, [animationStates]) as Record<string, "visible" | "hidden" | "instant">;

  useEffect(() => {
    prevScrollY.current = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > prevScrollY.current;
      setScrollDirection(isScrollingDown ? "down" : "up");
      prevScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      pos="relative"
      mt={0}
      pt={0}
      mb={0}
      pb={0}
      overflow="hidden"
      ref={(el) => {
        scheduleRef.current = el;
        if (componentRef) componentRef(el);
      }}
    >
      <TicketModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Box id="schedule-section" pos="absolute" top="0" h="1px" w="100%" />

      <Box {...commonStyles.container}>
        <Image
          src={HaikeiB}
          alt="BlueHaikei"
          {...commonStyles.backgroundImage}
        />
        <Box {...commonStyles.contentContainer}>
          <ScheduleHeader scrollDirection={scrollDirection} />

          <TicketSection
            refs={refs}
            animationStates={typedAnimationStates}
            slideInVariants={animationVariants.slideIn}
            scrollDirection={scrollDirection}
            elementsAnimatedState={elementsAnimatedState}
            setIsModalOpen={setIsModalOpen}
          />

          <PerformerSection scrollDirection={scrollDirection} />
        </Box>
      </Box>
    </Box>
  );
};

export default Schedule;
